"use client"

import Link from "next/link"
import { CheckCircle2 } from "lucide-react"
import StarRating from "./StarRating"

type ReviewCardProps = {
  review: {
    id: string
    overall_rating: number
    tags: string[]
    review_text?: string | null
    created_at: string
    // #553 — false means the review came in through the 7-day lenient
    // fallback with no host check-in. It still counts toward the rating.
    verified_attendance?: boolean
    user?: { full_name: string; avatar_url?: string | null; member_id?: string | null }
    event?: { title: string; start_datetime?: string | null }
  }
  showEventName?: boolean
  compact?: boolean
}

const TAG_LABELS: Record<string, string> = {
  TRANSFORMATIVE: "Transformative",
  INSPIRING: "Inspiring",
  GROUNDING: "Grounding",
  FUN: "Fun",
  DEEP: "Deep",
  EDUCATIONAL: "Educational",
  HEALING: "Healing",
  RELAXING: "Relaxing",
  EMPOWERING: "Empowering",
  PLAYFUL: "Playful",
  INTIMATE: "Intimate",
  CREATIVE: "Creative",
  VERY_PRESENT: "Very Present",
  CLEAR_GUIDANCE: "Clear Guidance",
  SUPPORTIVE: "Supportive",
  AUTHENTIC: "Authentic",
  PROFESSIONAL: "Professional",
  WARM: "Warm",
  INTUITIVE: "Intuitive",
  KNOWLEDGEABLE: "Knowledgeable",
  COMPASSIONATE: "Compassionate",
  WELCOMING_GROUP: "Welcoming Group",
  SAFE_SPACE: "Safe Space",
  GREAT_CONNECTIONS: "Great Connections",
  INCLUSIVE: "Inclusive",
  INTIMATE_GROUP: "Intimate Group",
  DIVERSE: "Diverse",
}

function formatEventLabel(title: string, dateStr?: string | null): string {
  if (!dateStr) return title
  const date = new Date(dateStr)
  const month = date.toLocaleString("en-US", { month: "long" })
  const year = date.getFullYear()
  return `${title} \u2013 ${month} ${year}`
}

export default function ReviewCard({ review, showEventName = false, compact = false }: ReviewCardProps) {
  return (
    <div className={`${compact ? "w-[220px] flex-shrink-0" : "w-full"} p-4 rounded-lg border border-neutral-200 bg-neutral-50`}>
      <div className="flex items-center justify-between mb-2">
        <StarRating value={review.overall_rating} size="sm" readOnly />
        <span className="text-xs text-neutral">
          {new Date(review.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </span>
      </div>

      {showEventName && review.event && (
        <p className="text-xs font-medium text-[#DE866D] mb-1 line-clamp-1">
          {formatEventLabel(review.event.title, review.event.start_datetime)}
        </p>
      )}

      {review.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {review.tags.slice(0, compact ? 3 : 6).map((tag) => (
            <span key={tag} className="px-1.5 py-0.5 text-[10px] rounded-full bg-[#DE866D]/10 text-[#DE866D] font-medium">
              {TAG_LABELS[tag] || tag}
            </span>
          ))}
        </div>
      )}

      {review.review_text && (
        <p className={`text-xs text-neutral leading-relaxed ${compact ? "line-clamp-3" : "line-clamp-5"} mb-2`}>
          {review.review_text}
        </p>
      )}

      {review.verified_attendance !== undefined && (
        <p className="flex items-center gap-1 text-[10px] mb-1">
          {review.verified_attendance ? (
            <>
              <CheckCircle2 className="h-3 w-3 text-[#75A096]" aria-hidden="true" />
              <span className="text-[#75A096] font-medium">Verified attendee</span>
            </>
          ) : (
            <span className="text-neutral/70">Unverified</span>
          )}
        </p>
      )}

      {review.user && (
        <p className="text-xs font-medium text-neutral-emphasis">
          {review.user.member_id ? (
            // #17 — reviewers with a public member profile link to it; the
            // API only sends member_id when the profile is public.
            <Link
              href={`/member-profile/${review.user.member_id}`}
              className="hover:underline focus-visible:underline"
            >
              {review.user.full_name}
            </Link>
          ) : (
            review.user.full_name
          )}
        </p>
      )}
    </div>
  )
}
